import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useBranches } from './publicApi';
import { todayIso } from '../shopTime';
import { SERVICE_IMAGES } from '../catalogImages';
import SaantiMenu from './SaantiMenu';
import SpaArrow from './SpaArrow';
import {
  bookingPathForBrand,
  branchForBrand,
  brandPath,
  formatThaiPhoneDisplay,
  locationForBrand,
  photosForBrand,
  PUBLIC_LOCATIONS,
  publicBrandFromSearch,
  usePublicBrand,
} from './PublicBrand';
import Seo, {
  localBusinessSchemas,
  organizationSchema,
  websiteSchema,
} from './Seo';
import usePublicServices from './usePublicServices';
import './spa.css';

const money = (n) => new Intl.NumberFormat('th-TH').format(Number(n || 0));
export const image = (number) => `/spa-images/saanti-${String(number).padStart(3, '0')}.webp`;
const SAANTI_PHOTOS = photosForBrand('saanti');
const BOUTIQUE_PHOTOS = photosForBrand('saanti-boutique');
const NIRANA_PHOTOS = photosForBrand('nirana');

const PAGES = [
  { path: '/', label: 'Home', exact: true },
  { path: '/story', label: 'Our story' },
  { path: '/massage', label: 'About massage' },
  { path: '/services', label: 'Services' },
  { path: '/facilities', label: 'Facilities' },
  { path: '/contact', label: 'Contact' },
];

export const NIRANA_CONTACT = locationForBrand('nirana').contact;
export const BOUTIQUE_CONTACT = locationForBrand('saanti-boutique').contact;
export const SAANTI_CONTACT = locationForBrand('saanti').contact;

const cleanPublicText = (value) => (
  typeof value === 'string' && value.trim() && !/failed to fetch/i.test(value)
    ? value.trim()
    : ''
);

// The source folder contains adjacent frames from the same photo/video shoot.
// Taking the first six therefore repeated the storefront several times even
// though the filenames were different. Curate one distinct scene per tile.
const GALLERY_IMAGES = SAANTI_PHOTOS.gallery;

export { SERVICE_IMAGES };

export const SERVICE_COPY = {
  SAANTI_THAI: 'Rhythmic pressure, assisted stretching and attentive hands restore ease throughout the body.',
  DEEP_TISSUE: 'Focused pressure works into tired muscles and the places where the city tends to stay with you.',
  NECK_SHOULDER: 'A concentrated treatment for the neck, shoulders and upper back when time is short.',
  AROMA_OIL: 'Warm aromatic oil and long, flowing strokes create a slower, softer kind of restoration.',
  AROMA_COUPLE: 'A shared aromatherapy ritual prepared for two guests in the quiet of a private room.',
  HOT_STONE: 'Smooth heated stones warm the body while measured pressure releases deep-set tension.',
  FOOT: 'A grounding foot ritual that refreshes tired legs and settles the whole body.',
  VP5: 'Thai balm massage followed by a focused foot treatment for complete head-to-toe relief.',
};

const GUEST_REVIEWS = [
  {
    author: 'Emily',
    quote: 'The most relaxing massage of my life!! Love the minimal interior design, the service was incredibly professional. I would come back everyday if I could.',
  },
  {
    author: 'Nano',
    quote: 'A nice place to relax, would definitely recommend if you’re around here.',
  },
  {
    author: 'Yoyo',
    quote: 'Lovely place in Song Wat—super clean, cozy, and the staff are all amazing. Every therapist is skilled and friendly. Feels like home every time I visit.',
  },
  {
    author: 'Kikkchy',
    quote: 'Such a nice place! The service was really good and the staff were sooo nice, which made me feel super welcome the whole time. I left feeling totally relaxed, like all my stress melted away. Also, the crackers were delicious!! Didn’t expect that little treat but it was sooo good. Definitely coming back again!',
  },
  {
    author: 'Ploynapus',
    quote: 'สบายมากค่ะ บรรยากาศร้านเหมือนอยู่บ้านเลยค่ะ ชอบสถานที่มากๆ ขนมตอนนวดเสร็จอร่อย ได้ลูกประคบกลับบ้านอีก ชอบบบบบบบ',
  },
  {
    author: 'Kunyakorn',
    quote: 'Hidden gem for true relaxation. From the moment I stepped through the door, it felt like I’d entered a little sanctuary tucked away from the chaos. The ambience was warm, inviting, and thoughtfully designed—soft lighting, gentle aromas, and peaceful music that instantly put me at ease. Highly recommended!!',
  },
  {
    author: 'Voranuch',
    quote: 'It’s a nice place for relaxing. Good price and good massage, staff are very professional. Highly recommended!!',
  },
  {
    author: 'Siyapat',
    quote: 'พนักงานนวดฝีมือดีมากกก สถานที่สงบ สะอาด สวยงาม เหมาะกับการมาผ่อนคลายจากความวุ่นวายใจกลางเมือง',
  },
  {
    author: 'Ying',
    quote: 'ร้านสวย อยากนวดพอดี ดีเกินคาด หมอนวดนวดสบายๆ บรรยากาศอบอุ่น พนักงานให้คำแนะนำดีมาก จะมาใช้บริการอีกค่ะ',
  },
  {
    author: 'Kavee',
    quote: 'The place is cozy and clean. I had two hours of Thai massage—so relaxing. The therapist was good and professional. Worth the money; I’d love to come back.',
  },
  {
    author: 'Ken',
    quote: 'ศานติ (SAANTI) วันนี้มาเดินเล่นแถวทรงวาด เดินจนเมื่อย เลยลองหาร้านนวดดู แล้วบังเอิญมาเจอร้านนี้ เป็นร้านนวดไทยเปิดใหม่ บรรยากาศดีมาก เลยพาคุณแฟนเข้ามาใช้บริการ ร้านตกแต่งสวย สะอาด และดูมืออาชีพ พนักงานน่ารัก เป็นกันเองสุดๆ รู้สึกผ่อนคลายตั้งแต่ก้าวแรกที่เดินเข้าไป ใครกำลังมองหาร้านนวดดีๆ สบายตัว แนะนำที่นี่เลยครับ ประทับใจแน่นอน',
  },
  {
    author: 'Patchara',
    quote: 'สถานที่สะอาด ตกแต่งสวย พนักงานบริการดี หมอนวดนวดดีมากๆๆๆ',
  },
];

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
};

export function SpaNav({ booking = false }) {
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const { brand, selectBrand } = usePublicBrand();
  const [open, setOpen] = useState(false);
  const activeLocation = locationForBrand(brand);
  const pages = PAGES;
  const isPageActive = (page) => {
    const [pagePath, pageHash = ''] = page.path.split('#');

    if (pageHash) {
      return pathname === pagePath && hash === `#${pageHash}`;
    }

    return page.exact
      ? pathname === page.path && !hash
      : pathname.startsWith(page.path);
  };
  const chooseBrand = (nextBrand) => {
    selectBrand(nextBrand);
    setOpen(false);
    navigate(brandPath('/', nextBrand));
  };

  return (
    <nav className={`spa-nav${booking ? ' booking-nav' : ''}`}>
      <div className="spa-utility">
        <div className="wrap">
          <span>{activeLocation.kicker}</span>
          <span>Open daily · 10:00–22:00</span>
        </div>
      </div>

      <div className="wrap spa-nav-main">
        <div className="spa-brand-pair" role="group" aria-label="Choose massage property">
          {PUBLIC_LOCATIONS.map((location, index) => (
            <span className="spa-brand-choice-wrap" key={location.id}>
              {index > 0 && <i aria-hidden="true" />}
              <button
                type="button"
                className={`spa-brand-choice ${location.id}${brand === location.id ? ' active' : ''}`}
                aria-label={`View ${location.label}`}
                aria-pressed={brand === location.id}
                onClick={() => chooseBrand(location.id)}
              >
                {location.id === 'saanti' && (
                  <img src="/spa-images/saanti-official-logo.png" alt="SAANTI Thai Massage" decoding="async" />
                )}
                {location.id === 'saanti-boutique' && (
                  <img className="boutique-wordmark" src="/spa-images/saanti-boutique-logo.png" alt="SAANTI Boutique Thai Massage House" decoding="async" />
                )}
                {location.id === 'nirana' && (
                  <img
                    className="nirana-wordmark"
                    src="/spa-images/nirana-master-wordmark.png"
                    alt="NIRANA Massage & Spa"
                    decoding="async"
                  />
                )}
              </button>
            </span>
          ))}
        </div>

        <button
          type="button"
          className="spa-menu-toggle"
          aria-label="Toggle navigation"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          <i />
          <i />
        </button>

        <div className={`spa-nav-links${open ? ' open' : ''}`}>
          {pages.map((page) => (
            <Link
              key={page.path}
              to={brandPath(page.path, brand)}
                className={isPageActive(page) ? 'on' : ''}
              onClick={() => setOpen(false)}
            >
              {page.label}
            </Link>
          ))}
          <button className="spa-cta compact" onClick={() => navigate(brandPath('/book', brand))}>
            Book now
          </button>
        </div>
      </div>
    </nav>
  );
}

export function SpaFooter() {
  const navigate = useNavigate();
  const { branches } = useBranches();
  const { brand } = usePublicBrand();
  const activeBranch = branchForBrand(branches, brand);
  const bookPath = bookingPathForBrand(branches, brand);
  const location = locationForBrand(brand);
  const activeBranchName = location.branchName;
  const activeBranchAddress = cleanPublicText(activeBranch?.address);

  if (brand === 'saanti-boutique') {
    return (
      <footer className="spa-footer boutique-footer">
        <div className="wrap">
          <div className="spa-footer-brand boutique-footer-brand">
            <img src="/spa-images/saanti-boutique-logo-light.png" alt="SAANTI Boutique Thai Massage House" loading="lazy" decoding="async" />
            <p>A quiet Thai massage retreat inside Baan 2459 Heritage Boutique Hotel, steps from Yaowarat.</p>
          </div>
          <div className="cols">
            <div>
              <h4>Explore</h4>
              <Link to={brandPath('/', brand)}>Home</Link>
              <Link to={brandPath('/story', brand)}>Our story</Link>
              <Link to={brandPath('/massage', brand)}>About massage</Link>
              <Link to={brandPath('/services', brand)}>Services</Link>
              <Link to={brandPath('/facilities', brand)}>Facilities</Link>
              <Link to={brandPath('/contact', brand)}>Contact us</Link>
              <button type="button" onClick={() => navigate(bookPath)}>Book a treatment</button>
            </div>
            <div>
              <h4>Visit</h4>
              <p>
                {activeBranchName}
                <br />{BOUTIQUE_CONTACT.address}
              </p>
            </div>
            <div>
              <h4>Connect</h4>
              <a href={BOUTIQUE_CONTACT.phoneHref}>{BOUTIQUE_CONTACT.phoneDisplay}</a>
              <a href={BOUTIQUE_CONTACT.lineHref} target="_blank" rel="noreferrer">LINE · {BOUTIQUE_CONTACT.lineDisplay}</a>
              <a href={BOUTIQUE_CONTACT.instagramHref} target="_blank" rel="noreferrer">Instagram · {BOUTIQUE_CONTACT.instagramDisplay}</a>
              <p>Facebook · {BOUTIQUE_CONTACT.facebookDisplay}</p>
              <a href={BOUTIQUE_CONTACT.emailHref}>{BOUTIQUE_CONTACT.emailDisplay}</a>
              <p>Open daily {BOUTIQUE_CONTACT.hours}</p>
            </div>
          </div>
          <div className="base">© {todayIso().slice(0, 4)} SAANTI Boutique. All rights reserved.</div>
        </div>
      </footer>
    );
  }

  if (brand === 'nirana') {
    return (
      <footer className="spa-footer nirana-footer">
        <div className="wrap">
          <div className="spa-footer-brand">
            <img src="/spa-images/nirana-master-wordmark-light.png" alt="NIRANA Massage & Spa" loading="lazy" decoding="async" />
            <p>Thai massage, thoughtful body rituals and private-room treatments in Bangkok.</p>
          </div>
          <div className="cols">
            <div>
              <h4>Explore</h4>
              <Link to={brandPath('/', brand)}>Home</Link>
              <Link to={brandPath('/story', brand)}>Our story</Link>
              <Link to={brandPath('/massage', brand)}>About massage</Link>
              <Link to={brandPath('/services', brand)}>Services</Link>
              <Link to={brandPath('/facilities', brand)}>Facilities</Link>
              <Link to={brandPath('/contact', brand)}>Contact us</Link>
              <button type="button" onClick={() => navigate(bookPath)}>Book a treatment</button>
            </div>
            <div>
              <h4>Visit</h4>
              <p>
                {activeBranchName}
                <br />{NIRANA_CONTACT.address}
              </p>
            </div>
            <div>
              <h4>Connect</h4>
              <a href={NIRANA_CONTACT.phoneHref}>{NIRANA_CONTACT.phoneDisplay}</a>
              <a href={NIRANA_CONTACT.lineHref} target="_blank" rel="noreferrer">LINE · {NIRANA_CONTACT.lineDisplay}</a>
              <a href={NIRANA_CONTACT.instagramHref} target="_blank" rel="noreferrer">Instagram · {NIRANA_CONTACT.instagramDisplay}</a>
              <p>Facebook · {NIRANA_CONTACT.facebookDisplay}</p>
              <a href={NIRANA_CONTACT.emailHref}>{NIRANA_CONTACT.emailDisplay}</a>
              <p>Open daily {NIRANA_CONTACT.hours}</p>
            </div>
          </div>
          <div className="base">© {todayIso().slice(0, 4)} NIRANA Massage &amp; Spa. All rights reserved.</div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="spa-footer">
      <div className="wrap">
        <div className="spa-footer-brand">
          <img
            src="/spa-images/saanti-official-logo-light.png"
            alt="SAANTI Thai Massage"
            width="594"
            height="482"
            loading="lazy"
            decoding="async"
          />
          <p>Quiet rooms, attentive hands and Thai tradition in the heart of Bangkok.</p>
        </div>

        <div className="cols">
          <div>
            <h4>Explore</h4>
            <Link to={brandPath('/', brand)}>Home</Link>
            <Link to={brandPath('/story', brand)}>Our story</Link>
            <Link to={brandPath('/massage', brand)}>About massage</Link>
            <Link to={brandPath('/services', brand)}>Services</Link>
            <Link to={brandPath('/facilities', brand)}>Facilities</Link>
            <Link to={brandPath('/contact', brand)}>Contact us</Link>
            <button type="button" onClick={() => navigate(bookPath)}>Book a treatment</button>
          </div>
          <div>
            <h4>Visit</h4>
            <p>{activeBranchName}</p>
            <p>{activeBranchAddress || '981 Song Wat Road'}<br />Samphanthawong, Bangkok 10100</p>
          </div>
          <div>
            <h4>Contact</h4>
            <a href="mailto:sales.saanti@gmail.com">sales.saanti@gmail.com</a>
            <a href={SAANTI_CONTACT.phoneHref}>{SAANTI_CONTACT.phoneDisplay}</a>
            <p>Open daily {activeBranch?.hours || '10:00 - 22:00'}</p>
            <p>Walk-ins are always welcome.</p>
            <div className="spa-socials" aria-label="SAANTI social media">
              <a
                href="https://www.instagram.com/saanti.thaimassage/"
                target="_blank"
                rel="noreferrer"
                aria-label="SAANTI on Instagram"
              >
                Instagram
              </a>
              <a
                href="https://www.facebook.com/people/Saanti-Thai-Massage-Songwat/61574191362794/"
                target="_blank"
                rel="noreferrer"
                aria-label="SAANTI on Facebook"
              >
                Facebook
              </a>
              <a
                href="https://www.tiktok.com/@saantithaimassagesongwat"
                target="_blank"
                rel="noreferrer"
                aria-label="SAANTI on TikTok"
              >
                TikTok
              </a>
            </div>
          </div>
        </div>
        <div className="base">© {todayIso().slice(0, 4)} SAANTI Massage. All rights reserved.</div>
      </div>
    </footer>
  );
}

function SaantiHome() {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const {
    activeBranch,
    branchError,
    branches,
    propertyUnavailable,
    serviceError: error,
    services,
  } = usePublicServices('saanti');
  const [reviewIndex, setReviewIndex] = useState(0);
  const [reviewTransition, setReviewTransition] = useState(null);
  const reviewTimerRef = useRef(null);

  const loaded = Boolean(services || error || branchError);
  useEffect(() => {
    if (hash && loaded) scrollToSection(hash.slice(1));
  }, [hash, loaded]);

  const bookPath = bookingPathForBrand(branches, 'saanti');
  const book = () => navigate(bookPath);
  const galleryImages = GALLERY_IMAGES.slice(0, 6);
  const reviewLengthClass = (review) => review.quote.length > 300
    ? 'long'
    : review.quote.length > 180 ? 'medium' : '';
  const reviewIsThai = (review) => /[\u0E00-\u0E7F]/.test(review.quote);

  useEffect(() => () => window.clearTimeout(reviewTimerRef.current), []);

  const moveReview = (direction) => {
    if (reviewTransition) return;
    const nextIndex =
      (reviewIndex + direction + GUEST_REVIEWS.length) % GUEST_REVIEWS.length;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setReviewIndex(nextIndex);
      return;
    }

    setReviewTransition({ from: reviewIndex, to: nextIndex, direction });
    window.clearTimeout(reviewTimerRef.current);
    reviewTimerRef.current = window.setTimeout(() => {
      setReviewIndex(nextIndex);
      setReviewTransition(null);
    }, 680);
  };

  return (
    <div className="spa">
      <Seo
        title="SAANTI Thai Massage Song Wat | Traditional Massage Bangkok"
        description="Relax at SAANTI Thai Massage on Song Wat Road, Bangkok. Explore Thai massage, aroma oil, foot massage and more, with live prices and online booking."
        canonicalPath="/?brand=saanti"
        image={SAANTI_PHOTOS.hero}
        imageAlt="SAANTI Thai Massage in the historic Song Wat neighbourhood of Bangkok"
        structuredData={[
          websiteSchema(),
          organizationSchema(),
          ...localBusinessSchemas(activeBranch ? [activeBranch] : []),
        ]}
      />
      <SpaNav />

      <header className="spa-hero">
        <img
          src={SAANTI_PHOTOS.hero}
          alt="SAANTI therapist preparing a traditional Thai massage room"
          width="1748"
          height="1080"
          fetchPriority="high"
          decoding="async"
        />
        <div className="spa-hero-shade" />
        <div className="wrap spa-hero-content">
          <div className="spa-kicker">The art of Thai restoration</div>
          <h1>Come back<br />to yourself.</h1>
          <p>
            Traditional Thai massage, thoughtful rituals and a quiet place to
            breathe—just steps from Bangkok&apos;s historic Song Wat Road.
          </p>
          <div className="spa-hero-actions">
            <button className="spa-cta light" onClick={book}>Reserve a time</button>
            <Link to={brandPath('/services', 'saanti')}>
              Explore treatments <SpaArrow direction="down-right" />
            </Link>
          </div>
        </div>
        <div className="spa-hero-note">
          <span>01</span>
          <p>Restorative care<br />in the old town</p>
        </div>
      </header>

      <section className="spa-welcome" id="story">
        <div className="wrap">
          <div className="spa-section-label"><span>01</span> Our philosophy</div>
          <div className="spa-welcome-grid">
            <div className="spa-welcome-copy">
              <div className="eyebrow">The wonder of slowing down</div>
              <h2>A calmer rhythm,<br />held with care.</h2>
              <p className="lead">
                Our journey began with a deep passion for wellness and holistic
                healing, shaped by more than a decade of experience in the
                service industry and a heartfelt dedication to therapeutic care.
              </p>
              <p>
                That vision now lives as SAANTI Thai Massage, tucked inside a
                beautifully preserved heritage building in the heart of Song
                Wat Road—a serene retreat where history, healing and heartfelt
                hospitality come together.
              </p>
              <Link className="spa-text-link" to={brandPath('/story', 'saanti')}>
                Read our story <SpaArrow />
              </Link>
            </div>
            <div className="spa-welcome-images">
              <img className="large" src={SAANTI_PHOTOS.welcome[0]} alt="Sunlit traditional Thai massage room at SAANTI" loading="lazy" decoding="async" />
              <img className="small" src={SAANTI_PHOTOS.welcome[1]} alt="SAANTI storefront on Song Wat Road" loading="lazy" decoding="async" />
              <div className="spa-stamp">
                <b>SAANTI</b>
                <span>Bangkok · Song Wat</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="spa-section spa-treatments" id="treatments">
        <div className="wrap">
          <div className="spa-section-label light"><span>02</span> Our treatments</div>
          <div className="spa-title-row">
            <div>
              <div className="eyebrow">Highly recommended</div>
              <h2>Choose what your<br />body needs today.</h2>
            </div>
            <p>
              Every price and duration is live from our booking system. Select
              a treatment and we will show you the available times.
            </p>
          </div>

          {(error || branchError) && <div className="spa-error">{error || branchError}</div>}
          {propertyUnavailable && !branchError && (
            <div className="spa-error">SAANTI Thai Massage is not accepting online bookings right now.</div>
          )}
          {!services && !error && !branchError && !propertyUnavailable && <div className="spa-loading">Loading treatments…</div>}

          <div className="spa-treatment-grid">
            {(services || []).slice(0, 4).map((service, index) => (
              <article className="spa-treatment-card" key={service.id}>
                <div className="spa-treatment-image">
                  <img
                    src={service.image_url || SAANTI_PHOTOS.treatments[index % SAANTI_PHOTOS.treatments.length] || SERVICE_IMAGES[service.code]}
                    alt={`${service.name_en} at SAANTI`}
                    loading="lazy"
                    decoding="async"
                  />
                  <span>{String(index + 1).padStart(2, '0')}</span>
                </div>
                <div className="spa-treatment-body">
                  <div className="spa-treatment-heading">
                    <div>
                      <h3>{service.name_en}</h3>
                      {service.name_th && <div className="th">{service.name_th}</div>}
                    </div>
                    <button aria-label={`Book ${service.name_en}`} onClick={book}><SpaArrow direction="up-right" /></button>
                  </div>
                  <p>{service.description || SERVICE_COPY[service.code] || 'A restorative treatment delivered with care and attention.'}</p>
                  <div className="spa-prices">
                    {(service.variants || []).map((variant) => (
                      <span className="spa-price" key={variant.id}>
                        {variant.durationMinutes} min <b>{money(variant.price)} ฿</b>
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
          <div className="spa-center-action">
            <Link className="spa-cta gold spa-button-link" to={brandPath('/services', 'saanti')}>
              View all services
            </Link>
          </div>
        </div>
      </section>

      <section className="spa-section spa-gallery-section" id="gallery">
        <div className="wrap">
          <div className="spa-section-label"><span>03</span> The SAANTI experience</div>
          <div className="spa-title-row dark">
            <div>
              <div className="eyebrow">A quiet room in a loud city</div>
              <h2>Warm light.<br />Unhurried hands.</h2>
            </div>
            <p>
              From the first cup of tea to the final warm towel, every detail is
              designed to let the outside world soften for a while.
            </p>
          </div>
          <div className="spa-gallery">
            {galleryImages.map((src, index) => (
              <figure key={src}>
                <img src={src} alt={`Inside SAANTI Massage ${index + 1}`} loading="lazy" decoding="async" />
              </figure>
            ))}
          </div>
          <div className="spa-center-action">
            <Link className="spa-text-link" to={brandPath('/facilities', 'saanti')}>
              Explore our facilities <SpaArrow />
            </Link>
          </div>
        </div>
      </section>

      <section className="spa-testimonial" id="guest-words">
        <div className="wrap">
          <div className="spa-section-label light"><span>04</span> Guest impression</div>
          <div className="spa-testimonial-grid">
            <div>
              <div className="eyebrow">Kind words from our guests</div>
              <h2>How SAANTI<br />felt to them.</h2>
            </div>
            <figure>
              <div className="spa-review-quote-stage" aria-live="polite" aria-atomic="true">
                {GUEST_REVIEWS.map((review, index) => {
                  const isCurrent = !reviewTransition && index === reviewIndex;
                  const isLeaving = reviewTransition?.from === index;
                  const isEntering = reviewTransition?.to === index;
                  const directionClass = reviewTransition?.direction === -1 ? ' previous' : ' next';
                  const motionClass = isLeaving
                    ? ` is-visible is-leaving${directionClass}`
                    : isEntering
                      ? ` is-visible is-entering${directionClass}`
                      : isCurrent ? ' is-visible' : '';
                  const thai = reviewIsThai(review);
                  return (
                    <blockquote
                      key={review.author}
                      className={`spa-review-copy ${reviewLengthClass(review)}${
                        thai ? ' thai' : ''}${motionClass}`.trim()}
                      lang={thai ? 'th' : 'en'}
                      aria-hidden={index !== (reviewTransition?.to ?? reviewIndex)}
                    >
                      “{review.quote}”
                    </blockquote>
                  );
                })}
              </div>
              <div className="spa-review-footer">
                <div className="spa-review-author-stage">
                  {GUEST_REVIEWS.map((review, index) => {
                    const isCurrent = !reviewTransition && index === reviewIndex;
                    const isLeaving = reviewTransition?.from === index;
                    const isEntering = reviewTransition?.to === index;
                    const directionClass = reviewTransition?.direction === -1 ? ' previous' : ' next';
                    const motionClass = isLeaving
                      ? ` is-visible is-leaving${directionClass}`
                      : isEntering
                        ? ` is-visible is-entering${directionClass}`
                        : isCurrent ? ' is-visible' : '';
                    return (
                      <figcaption
                        key={review.author}
                        className={`spa-review-author${motionClass}`}
                        aria-hidden={index !== (reviewTransition?.to ?? reviewIndex)}
                      >
                        <span>{review.author}</span>
                        <small>Google Reviews</small>
                      </figcaption>
                    );
                  })}
                </div>
                <div className="spa-review-controls">
                  <button
                    type="button"
                    aria-label="Previous guest review"
                    disabled={Boolean(reviewTransition)}
                    onClick={() => moveReview(-1)}
                  >
                    <SpaArrow direction="left" />
                  </button>
                  <span>
                    {String((reviewTransition?.to ?? reviewIndex) + 1).padStart(2, '0')}
                    {' '}/ {GUEST_REVIEWS.length}
                  </span>
                  <button
                    type="button"
                    aria-label="Next guest review"
                    disabled={Boolean(reviewTransition)}
                    onClick={() => moveReview(1)}
                  >
                    <SpaArrow />
                  </button>
                </div>
              </div>
            </figure>
          </div>
        </div>
      </section>

      <section className="spa-section spa-menu-section" id="menu">
        <div className="wrap">
          <div className="spa-section-label"><span>05</span> Plan your visit</div>
          <div className="spa-title-row dark">
            <div>
              <div className="eyebrow">Explore before you arrive</div>
              <h2>Our menu &amp;<br />signature packages.</h2>
            </div>
            <p>
              Browse the full treatment menu or discover combinations created
              for a longer, deeper pause.
            </p>
          </div>
          <SaantiMenu
            bookPath={bookPath}
            branchError={branchError}
            error={error}
            loading={!services && !error && !branchError && !propertyUnavailable}
            propertyUnavailable={propertyUnavailable}
            services={services || []}
          />
        </div>
      </section>

      <section className="spa-location" id="locations">
        <img src={SAANTI_PHOTOS.contact} alt="The entrance to SAANTI Massage on Song Wat Road" loading="lazy" decoding="async" />
        <div className="spa-location-shade" />
        <div className="wrap">
          <div className="spa-location-card">
            <div className="spa-section-label light"><span>06</span> Find us</div>
            {activeBranch ? (
              <div className="spa-location-branch" key={activeBranch.code}>
                <h2>SAANTI</h2>
                <p>{activeBranch.address || '981 Song Wat Road, Samphanthawong, Bangkok 10100'}</p>
                <dl>
                  <div><dt>Hours</dt><dd>Daily {activeBranch.hours}</dd></div>
                  {activeBranch.phone && (
                    <div>
                      <dt>Telephone</dt>
                      <dd>
                        <a href={`tel:${formatThaiPhoneDisplay(activeBranch.phone).replace(/\s/g, '')}`}>
                          {formatThaiPhoneDisplay(activeBranch.phone)}
                        </a>
                      </dd>
                    </div>
                  )}
                </dl>
                <div className="spa-location-actions">
                  <button className="spa-cta light" onClick={book}>
                    Book SAANTI
                  </button>
                  <Link className="spa-location-link" to={brandPath('/contact', 'saanti')}>Contact &amp; directions <SpaArrow /></Link>
                </div>
              </div>
            ) : Array.isArray(branches) && !branchError ? (
              <div className="spa-error">SAANTI Thai Massage is not accepting online bookings right now.</div>
            ) : null}
          </div>
        </div>
      </section>

      <SpaFooter />
    </div>
  );
}

function PublicPropertyChooser() {
  const { selectBrand } = usePublicBrand();

  return (
    <main className="spa spa-property-chooser">
      <Seo
        title="SAANTI & NIRANA Wellness Properties | Bangkok"
        description="Choose between SAANTI Thai Massage, SAANTI Boutique and NIRANA Massage & Spa in Bangkok."
        canonicalPath="/"
        image={SAANTI_PHOTOS.chooser}
        imageAlt="SAANTI and NIRANA wellness properties in Bangkok"
        structuredData={[websiteSchema(), organizationSchema()]}
      />
      <section className="property-chooser-grid" aria-label="Choose a wellness property">
        {PUBLIC_LOCATIONS.map((location, index) => (
          <Link
            className={`property-choice property-choice-${location.id}`}
            key={location.id}
            to={brandPath('/', location.id)}
            onClick={() => selectBrand(location.id)}
          >
            {location.image && <img src={location.image} alt="" decoding="async" />}
            <span className="property-choice-shade" aria-hidden="true" />
            <span className="property-choice-number">0{index + 1}</span>
            <span className="property-choice-copy">
              <span className="property-choice-kicker">{location.kicker}</span>
              {location.id === 'saanti' && (
                <img className="property-choice-logo saanti" src="/spa-images/saanti-official-logo-light.png" alt={location.label} />
              )}
              {location.id === 'nirana' && (
                <img className="property-choice-logo nirana" src="/spa-images/nirana-master-wordmark-light.png" alt={location.label} />
              )}
              {location.id === 'saanti-boutique' && (
                <img className="property-choice-logo boutique" src="/spa-images/saanti-boutique-logo.png" alt="SAANTI Boutique Thai Massage House" />
              )}
              <p>{location.description}</p>
              <span className="property-choice-cta">
                View {location.shortLabel} <SpaArrow />
              </span>
            </span>
          </Link>
        ))}
      </section>
    </main>
  );
}

function BoutiqueHome() {
  const {
    activeBranch,
    branchError,
    branches,
    propertyUnavailable: unavailable,
    serviceError,
    services,
  } = usePublicServices('saanti-boutique');
  const bookPath = bookingPathForBrand(branches, 'saanti-boutique');
  const treatmentImages = BOUTIQUE_PHOTOS.treatments;
  const galleryImages = BOUTIQUE_PHOTOS.gallery;

  return (
    <div className="spa spa-boutique">
      <Seo
        title="SAANTI Boutique Thai Massage House | Yaowarat Bangkok"
        description="Traditional Thai massage in a peaceful century-old home inside Baan 2459 Heritage Boutique Hotel, steps from Yaowarat."
        canonicalPath="/?brand=saanti-boutique"
        image={BOUTIQUE_PHOTOS.hero}
        imageAlt="SAANTI Boutique Thai Massage House in old Bangkok"
        structuredData={activeBranch ? localBusinessSchemas([activeBranch]) : []}
      />
      <SpaNav />

      <header className="spa-hero boutique-home-hero">
        <img
          src={BOUTIQUE_PHOTOS.hero}
          alt="The sunlit foot massage lounge at SAANTI Boutique"
          width="2200"
          height="1467"
          fetchPriority="high"
          decoding="async"
        />
        <div className="spa-hero-shade" />
        <div className="wrap spa-hero-content">
          <div className="spa-kicker">SAANTI Boutique · Yaowarat</div>
          <h1>A quiet retreat<br />in old Bangkok.</h1>
          <p>
            Just steps from Yaowarat, traditional Thai massage meets the serenity
            of a historic setting inside Baan 2459 Heritage Boutique Hotel.
          </p>
          <div className="spa-hero-actions">
            <Link className="spa-cta light spa-button-link" to={bookPath}>Reserve a time</Link>
            <Link to={brandPath('/services', 'saanti-boutique')}>Explore treatments <SpaArrow direction="down-right" /></Link>
          </div>
          {unavailable && (
            <div className="spa-error">SAANTI Boutique is not accepting online bookings right now.</div>
          )}
        </div>
        <div className="spa-hero-note">
          <span>01</span>
          <p>Step in · slow down<br />feel restored</p>
        </div>
      </header>

      <section className="spa-welcome" id="story">
        <div className="wrap">
          <div className="spa-section-label"><span>01</span> Our philosophy</div>
          <div className="spa-welcome-grid">
            <div className="spa-welcome-copy">
              <div className="eyebrow">A quiet retreat in old Bangkok</div>
              <h2>Traditional care,<br />in a century-old home.</h2>
              <p className="lead">
                Peaceful, intimate, and away from the city’s rhythm, SAANTI offers
                more than a massage—it is a moment to slow down, unwind, and
                reconnect with a sense of calm.
              </p>
              <p>
                Just steps from Yaowarat, the art of traditional Thai massage
                meets the serenity of Baan 2459 Heritage Boutique Hotel.
              </p>
              <Link className="spa-text-link" to={brandPath('/story', 'saanti-boutique')}>
                Read our story <SpaArrow />
              </Link>
            </div>
            <div className="spa-welcome-images boutique-welcome-images">
              <img className="large" src={BOUTIQUE_PHOTOS.welcome[0]} alt="Heritage Thai massage room at SAANTI Boutique" loading="lazy" decoding="async" />
              <img className="small" src={BOUTIQUE_PHOTOS.welcome[1]} alt="A back massage treatment at SAANTI Boutique" loading="lazy" decoding="async" />
              <div className="spa-stamp">
                <b>SAANTI</b>
                <span>Boutique · Yaowarat</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="spa-section spa-treatments boutique-home-section" id="treatments">
        <div className="wrap">
          <div className="spa-section-label light"><span>02</span> Our treatments</div>
          <div className="spa-title-row">
            <div>
              <div className="eyebrow">Live treatment menu</div>
              <h2>Choose your Boutique<br />treatment.</h2>
            </div>
            <p>Every treatment, duration, and price below is activated specifically for SAANTI Boutique in the POS.</p>
          </div>

          {(serviceError || branchError) && <div className="spa-error">{serviceError || branchError}</div>}
          {unavailable && !branchError && (
            <div className="spa-error">SAANTI Boutique is not accepting online bookings right now.</div>
          )}
          {!services && !serviceError && !branchError && !unavailable && (
            <div className="spa-loading">Loading SAANTI Boutique treatments…</div>
          )}
          {services?.length === 0 && (
            <div className="spa-note">No treatments are currently activated online for SAANTI Boutique.</div>
          )}

          <div className="spa-treatment-grid boutique-live-grid">
            {(services || []).slice(0, 4).map((service, index) => (
              <article className="spa-treatment-card" key={service.id}>
                <div className="spa-treatment-image">
                  <img
                    src={service.image_url || treatmentImages[index % treatmentImages.length]}
                    alt={`${service.name_en} at SAANTI Boutique`}
                    loading="lazy"
                    decoding="async"
                  />
                  <span>{String(index + 1).padStart(2, '0')}</span>
                </div>
                <div className="spa-treatment-body">
                  <div className="spa-treatment-heading">
                    <div>
                      <h3>{service.name_en}</h3>
                      {service.name_th && <div className="th">{service.name_th}</div>}
                    </div>
                    <Link
                      className="spa-treatment-book-link"
                      aria-label={`Book ${service.name_en}`}
                      to={bookPath}
                    ><SpaArrow direction="up-right" /></Link>
                  </div>
                  <p>{service.description || 'The final editorial treatment description will be added with the approved Boutique materials.'}</p>
                  <div className="spa-prices">
                    {(service.variants || []).map((variant) => (
                      <span className="spa-price" key={variant.id}>
                        {variant.durationMinutes} min <b>{money(variant.price)} ฿</b>
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
          <div className="spa-center-action">
            <Link className="spa-cta gold spa-button-link" to={brandPath('/services', 'saanti-boutique')}>View live services</Link>
          </div>
        </div>
      </section>

      <section className="spa-section spa-gallery-section boutique-home-section" id="gallery">
        <div className="wrap">
          <div className="spa-section-label"><span>03</span> The SAANTI Boutique experience</div>
          <div className="spa-title-row dark">
            <div>
              <div className="eyebrow">A century-old Thai home</div>
              <h2>Heritage character.<br />Quiet, attentive care.</h2>
            </div>
            <p>From the leafy entrance to peaceful treatment rooms, each space preserves the intimate character of Baan 2459.</p>
          </div>
          <div className="spa-gallery">
            {galleryImages.map((src, index) => (
              <figure key={src}>
                <img src={src} alt={`Inside SAANTI Boutique ${index + 1}`} loading="lazy" decoding="async" />
              </figure>
            ))}
          </div>
          <div className="spa-center-action">
            <Link className="spa-text-link" to={brandPath('/facilities', 'saanti-boutique')}>
              Explore our facilities <SpaArrow />
            </Link>
          </div>
        </div>
      </section>

      <section className="spa-testimonial boutique-home-testimonial" id="guest-words">
        <div className="wrap">
          <div className="spa-section-label light"><span>04</span> Guest impression</div>
          <div className="spa-testimonial-grid">
            <div>
              <div className="eyebrow">Guest stories</div>
              <h2>How Boutique<br />felt to them.</h2>
            </div>
            <figure>
              <blockquote>Approved guest impressions and review excerpts will appear here soon.</blockquote>
              <figcaption><span>SAANTI Boutique guests</span><small>Reviews coming soon</small></figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section className="spa-section spa-menu-section boutique-home-section" id="menu">
        <div className="wrap">
          <div className="spa-section-label"><span>05</span> Plan your visit</div>
          <div className="spa-title-row dark">
            <div>
              <div className="eyebrow">Before you arrive</div>
              <h2>Our menu &amp;<br />live Boutique prices.</h2>
            </div>
            <p>Browse the full live treatment menu before planning your visit to our quiet heritage setting near Yaowarat.</p>
          </div>
          <SaantiMenu
            ariaLabel="SAANTI Boutique live treatment prices"
            bookPath={bookPath}
            bookingLabel="Book SAANTI Boutique"
            branchError={branchError}
            error={serviceError}
            loading={!services && !serviceError && !branchError && !unavailable}
            propertyLabel="SAANTI Boutique"
            propertyUnavailable={unavailable}
            services={services || []}
          />
        </div>
      </section>

      <section className="spa-location boutique-home-location" id="locations">
        <img src={BOUTIQUE_PHOTOS.contact} alt="The garden entrance to SAANTI Boutique" loading="lazy" decoding="async" />
        <div className="spa-location-shade" />
        <div className="wrap">
          <div className="spa-location-card">
            <div className="spa-section-label light"><span>06</span> Find us</div>
            <div className="spa-location-branch">
              <h2>SAANTI Boutique</h2>
              <p>{BOUTIQUE_CONTACT.address}</p>
              <dl>
                <div><dt>Hours</dt><dd>Daily {BOUTIQUE_CONTACT.hours}</dd></div>
                <div><dt>Telephone</dt><dd><a href={BOUTIQUE_CONTACT.phoneHref}>{BOUTIQUE_CONTACT.phoneDisplay}</a></dd></div>
                <div><dt>LINE</dt><dd><a href={BOUTIQUE_CONTACT.lineHref} target="_blank" rel="noreferrer">{BOUTIQUE_CONTACT.lineDisplay}</a></dd></div>
              </dl>
              <div className="spa-location-actions">
                {activeBranch ? (
                    <Link className="spa-cta light spa-button-link" to={bookPath}>Book Boutique</Link>
                ) : <span className="spa-error">Online booking is temporarily unavailable.</span>}
                <Link className="spa-location-link" to={brandPath('/contact', 'saanti-boutique')}>Contact &amp; directions <SpaArrow /></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SpaFooter />
    </div>
  );
}

function NiranaHome() {
  const {
    activeBranch,
    branchError,
    branches,
    propertyUnavailable,
    serviceError,
    services,
  } = usePublicServices('nirana');
  const bookPath = bookingPathForBrand(branches, 'nirana');
  const treatmentImages = NIRANA_PHOTOS.treatments;
  const galleryImages = NIRANA_PHOTOS.gallery.map((src, index) => [
    src,
    `${index + 1} of the NIRANA treatment rooms and facilities`,
  ]);

  return (
    <div className="spa spa-nirana">
      <Seo
        title="NIRANA Massage & Spa Bangkok | Thai Massage, Body & Facial Care"
        description="A quiet, spacious retreat in Song Wat with a complete range of massage and spa treatments. Step in, slow down and feel restored."
        canonicalPath="/?brand=nirana"
        image={NIRANA_PHOTOS.hero}
        imageAlt="Traditional Thai massage treatment at NIRANA Massage and Spa"
        structuredData={activeBranch ? localBusinessSchemas([activeBranch]) : []}
      />
      <SpaNav />

      <header className="spa-hero nirana-home-hero">
        <img
          src={NIRANA_PHOTOS.hero}
          alt="Traditional Thai massage treatment at NIRANA Massage and Spa"
          width="1920"
          height="1280"
          fetchPriority="high"
          decoding="async"
        />
        <div className="spa-hero-shade" />
        <div className="wrap spa-hero-content">
          <div className="spa-kicker">NIRANA Massage &amp; Spa</div>
          <h1>A calmer way<br />to feel restored.</h1>
          <p>
            A quiet space in the heart of Song Wat, with spacious interiors and
            a complete range of massage and spa treatments.
          </p>
          <div className="spa-hero-actions">
            <Link className="spa-cta light spa-button-link" to={bookPath}>Reserve a time</Link>
            <Link to={brandPath('/services', 'nirana')}>Explore treatments <SpaArrow direction="down-right" /></Link>
          </div>
        </div>
        <div className="spa-hero-note">
          <span>01</span>
          <p>Massage &amp; spa care<br />in one calm place</p>
        </div>
      </header>

      <section className="spa-welcome" id="story">
        <div className="wrap">
          <div className="spa-section-label"><span>01</span> Our philosophy</div>
          <div className="spa-welcome-grid">
            <div className="spa-welcome-copy">
              <div className="eyebrow">A quiet space in Song Wat</div>
              <h2>Step in. Slow down.<br />Feel restored.</h2>
              <p className="lead">
                NIRANA brings massage, body care and spa treatments together in
                a spacious setting created for a quieter pause in Song Wat.
              </p>
              <p>
                Spacious interiors and a complete range of treatments create a
                calm place to restore the body and step away from the city’s pace.
              </p>
              <Link className="spa-text-link" to={brandPath('/story', 'nirana')}>
                Read our story <SpaArrow />
              </Link>
            </div>
            <div className="spa-welcome-images">
              <img className="large" src={NIRANA_PHOTOS.welcome[0]} alt="A guest being welcomed at NIRANA" loading="lazy" decoding="async" />
              <img className="small" src={NIRANA_PHOTOS.welcome[1]} alt="Tea served after a NIRANA treatment" loading="lazy" decoding="async" />
              <div className="spa-stamp">
                <b>NIRANA</b>
                <span>Bangkok · Song Wat</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="spa-section spa-treatments" id="treatments">
        <div className="wrap">
          <div className="spa-section-label light"><span>02</span> Our treatments</div>
          <div className="spa-title-row">
            <div>
              <div className="eyebrow">Massage · body care · spa rituals</div>
              <h2>Choose what your<br />body needs today.</h2>
            </div>
            <p>Every treatment, duration, and price is live from the NIRANA branch in the booking system.</p>
          </div>

          {(serviceError || branchError) && <div className="spa-error">{serviceError || branchError}</div>}
          {propertyUnavailable && !branchError && (
            <div className="spa-error">NIRANA is not accepting online bookings right now.</div>
          )}
          {!services && !serviceError && !branchError && !propertyUnavailable && (
            <div className="spa-loading">Loading NIRANA treatments…</div>
          )}
          {services?.length === 0 && (
            <div className="spa-note">No treatments are currently activated online for NIRANA.</div>
          )}

          <div className="spa-treatment-grid">
            {(services || []).slice(0, 4).map((service, index) => (
              <article className="spa-treatment-card" key={service.id}>
                <div className="spa-treatment-image">
                  <img
                    src={service.image_url || treatmentImages[index % treatmentImages.length]}
                    alt={`${service.name_en} at NIRANA`}
                    loading="lazy"
                    decoding="async"
                  />
                  <span>{String(index + 1).padStart(2, '0')}</span>
                </div>
                <div className="spa-treatment-body">
                  <div className="spa-treatment-heading">
                    <div>
                      <h3>{service.name_en}</h3>
                      {service.name_th && <div className="th">{service.name_th}</div>}
                    </div>
                    <Link className="spa-treatment-book-link" aria-label={`Book ${service.name_en}`} to={bookPath}><SpaArrow direction="up-right" /></Link>
                  </div>
                  <p>{service.description || 'A restorative treatment delivered with attentive care.'}</p>
                  <div className="spa-prices">
                    {(service.variants || []).map((variant) => (
                      <span className="spa-price" key={variant.id}>
                        {variant.durationMinutes} min <b>{money(variant.price)} ฿</b>
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
          <div className="spa-center-action">
            <Link className="spa-cta gold spa-button-link" to={brandPath('/services', 'nirana')}>View all services</Link>
          </div>
        </div>
      </section>

      <section className="spa-section spa-gallery-section" id="gallery">
        <div className="wrap">
          <div className="spa-section-label"><span>03</span> The NIRANA experience</div>
          <div className="spa-title-row dark">
            <div>
              <div className="eyebrow">A quiet room in a loud city</div>
              <h2>Spacious rooms.<br />Unhurried care.</h2>
            </div>
            <p>From treatment rooms to the final cup of tea, every space is prepared for a slower, quieter pause.</p>
          </div>
          <div className="spa-gallery">
            {galleryImages.map(([src, alt]) => (
              <figure key={src}><img src={src} alt={alt} loading="lazy" decoding="async" /></figure>
            ))}
          </div>
          <div className="spa-center-action">
            <Link className="spa-text-link" to={brandPath('/facilities', 'nirana')}>
              Explore our facilities <SpaArrow />
            </Link>
          </div>
        </div>
      </section>

      <section className="spa-testimonial nirana-home-testimonial" id="guest-words">
        <div className="wrap">
          <div className="spa-section-label light"><span>04</span> Guest impression</div>
          <div className="spa-testimonial-grid">
            <div>
              <div className="eyebrow">Guest stories</div>
              <h2>How NIRANA<br />felt to them.</h2>
            </div>
            <figure>
              <blockquote>Guest impressions and approved review excerpts will appear here soon.</blockquote>
              <figcaption><span>NIRANA guests</span><small>Reviews coming soon</small></figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section className="spa-section spa-menu-section nirana-plan-section" id="menu">
        <div className="wrap">
          <div className="spa-section-label"><span>05</span> Plan your visit</div>
          <div className="spa-title-row dark">
            <div>
              <div className="eyebrow">Choose before you arrive</div>
              <h2>Our menu &amp;<br />live NIRANA prices.</h2>
            </div>
            <p>Browse the full live treatment menu or reserve a time directly with NIRANA.</p>
          </div>
          <SaantiMenu
            ariaLabel="NIRANA live treatment prices"
            bookPath={bookPath}
            bookingLabel="Book NIRANA"
            branchError={branchError}
            error={serviceError}
            loading={!services && !serviceError && !branchError && !propertyUnavailable}
            propertyLabel="NIRANA"
            propertyUnavailable={propertyUnavailable}
            services={services || []}
          />
        </div>
      </section>

      <section className="spa-location" id="locations">
        <img src={NIRANA_PHOTOS.contact} alt="NIRANA Massage and Spa reception in Song Wat" loading="lazy" decoding="async" />
        <div className="spa-location-shade" />
        <div className="wrap">
          <div className="spa-location-card">
            <div className="spa-section-label light"><span>06</span> Find us</div>
            <div className="spa-location-branch">
              <h2>NIRANA</h2>
              <p>{NIRANA_CONTACT.address}</p>
              <dl>
                <div><dt>Hours</dt><dd>Daily {NIRANA_CONTACT.hours}</dd></div>
                <div><dt>Telephone</dt><dd><a href={NIRANA_CONTACT.phoneHref}>{NIRANA_CONTACT.phoneDisplay}</a></dd></div>
                <div><dt>LINE</dt><dd><a href={NIRANA_CONTACT.lineHref} target="_blank" rel="noreferrer">{NIRANA_CONTACT.lineDisplay}</a></dd></div>
              </dl>
              <div className="spa-location-actions">
                {activeBranch ? (
                  <Link className="spa-cta light spa-button-link" to={bookPath}>Book NIRANA</Link>
                ) : <span className="spa-error">Online booking is temporarily unavailable.</span>}
                <Link className="spa-location-link" to={brandPath('/contact', 'nirana')}>Contact &amp; directions <SpaArrow /></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SpaFooter />
    </div>
  );
}

export default function SpaHome() {
  const { brand } = usePublicBrand();
  const { search } = useLocation();
  if (!publicBrandFromSearch(search)) return <PublicPropertyChooser />;

  const homes = {
    saanti: <SaantiHome />,
    'saanti-boutique': <BoutiqueHome />,
    nirana: <NiranaHome />,
  };
  return homes[brand] || <PublicPropertyChooser />;
}
